import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FukudaToshio from '../assets/福田敏男.png';
import KubotaNaoyuki from '../assets/久保田直行.png';
import XuChunshan from '../assets/许春山.png';
import TanakaOsamu from '../assets/田中理.png';
import ManabeTomoyuki from '../assets/真鍋智章.png';
import YoshidaKaede from '../assets/吉田楓.png';
import MatsumotoYu from '../assets/松本有.png';
import IgarashiAkio from '../assets/五十嵐章夫.png';
import '../styles/core-people.css';
import useRevealAnimation from '../hooks/useRevealAnimation';
import useDocumentTitle from '../hooks/useDocumentTitle';

type Person = {
    name: string;
    title?: string;
    lines: string[];
};

type TeamGroup = {
    title: string;
    members: Person[];
};

const normalizePersonName = (name: string) => name.replace(/[\s\u3000]+/g, '').toLowerCase();

const CorePeople: React.FC = () => {
    const { t } = useTranslation();
    const pageRef = useRef<HTMLDivElement>(null);

    useRevealAnimation(pageRef);
    useDocumentTitle(t('CorePeople.hero.title'));

    const featured = t('CorePeople.featured', { returnObjects: true }) as Person[];
    const industryMembers = t('CorePeople.industryMembers', { returnObjects: true }) as Person[];
    const technicalGroups = t('CorePeople.technicalGroups', { returnObjects: true }) as TeamGroup[];
    const operationsGroups = t('CorePeople.operationsGroups', { returnObjects: true }) as TeamGroup[];

    const imageMap: Record<string, string> = {
        [normalizePersonName('福田敏男')]: FukudaToshio,
        [normalizePersonName('福田 敏男')]: FukudaToshio,
        [normalizePersonName('Toshio Fukuda')]: FukudaToshio,
        [normalizePersonName('久保田直行')]: KubotaNaoyuki,
        [normalizePersonName('久保田 直行')]: KubotaNaoyuki,
        [normalizePersonName('Naoyuki Kubota')]: KubotaNaoyuki,
        [normalizePersonName('许春山')]: XuChunshan,
        [normalizePersonName('許春山')]: XuChunshan,
        [normalizePersonName('许 春山')]: XuChunshan,
        [normalizePersonName('許 春山')]: XuChunshan,
        [normalizePersonName('Xu Chunshan')]: XuChunshan,
        [normalizePersonName('田中理')]: TanakaOsamu,
        [normalizePersonName('田中 理')]: TanakaOsamu,
        [normalizePersonName('Osamu Tanaka')]: TanakaOsamu,
        [normalizePersonName('真鍋智章')]: ManabeTomoyuki,
        [normalizePersonName('真鍋 智章')]: ManabeTomoyuki,
        [normalizePersonName('真鍋智幸')]: ManabeTomoyuki,
        [normalizePersonName('真鍋 智幸')]: ManabeTomoyuki,
        [normalizePersonName('真锅智章')]: ManabeTomoyuki,
        [normalizePersonName('真锅 智章')]: ManabeTomoyuki,
        [normalizePersonName('真锅智幸')]: ManabeTomoyuki,
        [normalizePersonName('真锅 智幸')]: ManabeTomoyuki,
        [normalizePersonName('Tomoyuki Manabe')]: ManabeTomoyuki,
        [normalizePersonName('Yoshida Kaede')]: YoshidaKaede,
        [normalizePersonName('吉田楓')]: YoshidaKaede,
        [normalizePersonName('吉田 楓')]: YoshidaKaede,
        [normalizePersonName('松本有')]: MatsumotoYu,
        [normalizePersonName('松本 有')]: MatsumotoYu,
        [normalizePersonName('Yu Matsumoto')]: MatsumotoYu,
        [normalizePersonName('五十嵐章夫')]: IgarashiAkio,
        [normalizePersonName('五十嵐 章夫')]: IgarashiAkio,
        [normalizePersonName('五十嵐幸夫')]: IgarashiAkio,
        [normalizePersonName('五十嵐 幸夫')]: IgarashiAkio,
        [normalizePersonName('Akio Igarashi')]: IgarashiAkio,
        [normalizePersonName('Yukio Igarashi')]: IgarashiAkio
    };

    const renderMemberCard = (member: Person, className = '') => {
        const portrait = imageMap[normalizePersonName(member.name)];

        return (
            <article className={`core-people-member-card ${className}`.trim()} key={member.name}>
                <div className={`core-people-member-visual${portrait ? ' has-image' : ''}`}>
                    {portrait ? (
                        <img src={portrait} alt={member.name} className="core-people-member-image" />
                    ) : (
                        <span>{member.name.slice(0, 1)}</span>
                    )}
                </div>
                <div className="core-people-member-copy">
                    <h3>{member.name}</h3>
                    {member.title && <p className="core-people-member-title">{member.title}</p>}
                    <div className="core-people-member-lines">
                        {member.lines.length > 0 ? (
                            member.lines.map((line, index) => <p key={index}>{line}</p>)
                        ) : (
                            <p>&nbsp;</p>
                        )}
                    </div>
                </div>
            </article>
        );
    };

    return (
        <div className="core-people-page" ref={pageRef}>
            <section className="core-people-hero">
                <div className="core-people-hero-bg">
                    <div className="core-people-grid" />
                    <div className="core-people-glow" />
                </div>
                <div className="section-container core-people-hero-content">
                    <div className="core-people-hero-copy reveal">
                        <span className="core-people-kicker">{t('CorePeople.hero.kicker')}</span>
                        <h1 className="core-people-hero-title">{t('CorePeople.hero.title')}</h1>
                        <p className="core-people-hero-description">{t('CorePeople.hero.description')}</p>
                    </div>
                </div>
            </section>

            <section className="section-container core-people-section">
                <div className="section-header reveal">
                    <h2 className="section-title">{t('CorePeople.advisorTitle')}</h2>
                </div>
                <div className="core-people-featured-grid">
                    {featured.map((member, index) => (
                        <div key={member.name} className="reveal" style={{ transitionDelay: `${0.1 * (index + 1)}s` }}>
                            {renderMemberCard(member, index === 0 ? 'is-hero-card' : 'is-featured-card')}
                        </div>
                    ))}
                </div>
            </section>

            <section className="section-container core-people-section">
                <div className="section-header reveal">
                    <h2 className="section-title">{t('CorePeople.industryTitle')}</h2>
                </div>
                <div className="core-people-grid-layout">
                    {industryMembers.map((member, index) => (
                        <div key={member.name} className="reveal" style={{ transitionDelay: `${0.08 * (index + 1)}s` }}>
                            {renderMemberCard(member)}
                        </div>
                    ))}
                </div>
            </section>

            <section className="section-container core-people-section">
                <div className="section-header reveal">
                    <h2 className="section-title">{t('CorePeople.technicalTitle')}</h2>
                </div>
                <div className="core-people-group-stack">
                    {technicalGroups.map((group, groupIndex) => (
                        <section className="core-people-group-panel reveal" key={group.title} style={{ transitionDelay: `${0.1 * (groupIndex + 1)}s` }}>
                            <div className="core-people-group-head">
                                <h3>{group.title}</h3>
                            </div>
                            <div className="core-people-grid-layout core-people-grid-layout-two">
                                {group.members.map((member) => renderMemberCard(member))}
                            </div>
                        </section>
                    ))}
                </div>
            </section>

            <section className="section-container core-people-section">
                <div className="section-header reveal">
                    <h2 className="section-title">{t('CorePeople.operationsTitle')}</h2>
                </div>
                <div className="core-people-group-stack">
                    {operationsGroups.map((group, groupIndex) => (
                        <section className="core-people-group-panel reveal" key={group.title} style={{ transitionDelay: `${0.1 * (groupIndex + 1)}s` }}>
                            <div className="core-people-group-head">
                                <h3>{group.title}</h3>
                            </div>
                            <div className="core-people-grid-layout core-people-grid-layout-two">
                                {group.members.map((member) => renderMemberCard(member))}
                            </div>
                        </section>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default CorePeople;

